import { createApp, h, markRaw } from "vue";
import Toast, { TYPE } from "vue-toastification";
import vSelect from "vue-select";
import FloatingVue from "floating-vue";
import VueTelInput from "vue-tel-input";
import VueGtag from "vue-gtag";
import { createPinia } from "pinia";
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
import { enableDevValidations } from 'ag-grid-community';

import { Crisp } from "crisp-sdk-web";
import moment from "moment";
import "moment/dist/locale/fr";
import mitt from "mitt";
import "./vee-validate";
import { i18n } from "./i18n";

import { QuillEditor } from '@vueup/vue-quill'
import '@vueup/vue-quill/dist/vue-quill.snow.css';
import '@vueup/vue-quill/dist/vue-quill.bubble.css';

import { safeHtml } from './directives/safeHtml';
import App from "./App.vue";
import adminRouter from "./router/adminRouter";
import experienceRouter from "./router/experienceRouter";

// Impport style sheets
import "vue-select/dist/vue-select.css";
import "floating-vue/dist/style.css";
import "vue-tel-input/vue-tel-input.css";
import "flag-icons/css/flag-icons.min.css";
import "./assets/style/main.scss";

import { VueQueryPlugin } from '@tanstack/vue-query';
import { queryClient } from '@/queries/queryClient';

ModuleRegistry.registerModules([AllCommunityModule]);

if (import.meta.env.VITE_ENV !== 'production') {
    enableDevValidations();
}

// Define the router
const router = () => {
  const host = window.location.host;

  if (host.includes("experience")) {
    return experienceRouter;
  } else {
    return adminRouter;
  }
};

// Create instances
const app = createApp(App);
const pinia = createPinia();
const momentInstance = moment;
const emitter = mitt();
const crispInstance = Crisp;
const { t } = i18n.global;

app.directive('safe-html', safeHtml);

app.use(VueQueryPlugin, {
  queryClient
});

// Config some libraries
// |_ VSelect
vSelect.props.components.default = () => ({
  Deselect: {
    render: () => h("i", { class: "bi bi-x" }, ""),
  },
  OpenIndicator: {
    render: () => h("i", { class: "bi bi-chevron-down" }, ""),
  },
});
// |_ Deps inside pinia
pinia.use(({ store }) => {
  store.$moment = markRaw(momentInstance);
  store.$router = markRaw(router());
  store.$emitter = markRaw(emitter);
  store.$crisp = markRaw(crispInstance);
});
// |_ Tooltips
FloatingVue.options.themes.tooltip.distance = 10;
// |_ Moment
momentInstance.locale("fr");
// |_ Crisp
crispInstance.configure("d7ba28c0-d105-41c0-8d97-0678de81eaf8", {
  autoload: import.meta.env.VITE_ENV === "production",
});

// Use some libraries
app.use(pinia);
app.use(router());
app.use(i18n);
app.use(FloatingVue);
app.provide("moment", momentInstance);
app.provide("emitter", emitter);
app.provide("crisp", crispInstance);
app.use(Toast, {
  transition: "Vue-Toastification__fade",
  timeout: 5000,
  icon: {
    iconTag: "i",
    iconClass: "bi bi-info-circle",
  },
  toastDefaults: {
    [TYPE.ERROR]: {
      icon: {
        iconClass: "bi bi-x-circle",
      },
    },
    [TYPE.SUCCESS]: {
      icon: {
        iconClass: "bi-check2-circle",
      },
    },
    [TYPE.WARNING]: {
      icon: {
        iconClass: "bi bi-exclamation-circle",
      },
    },
  },
});
app.use(VueTelInput, {
  defaultCountry: "fr",
  preferredCountries: ["fr", "be", "lu", "ch"],
  dropdownOptions: {
    showSearchBox: true,
    showFlags: true,
  },
  inputOptions: {
    showDialCode: true,
    placeholder: t('other.form.phone.placeholder'),
  },
});
app.use(
  VueGtag,
  {
    enabled: import.meta.env.VITE_ENV === "production",
    pageTrackerTemplate(to) {
      return {
        page_title: to.meta.document_title,
        page_path: to.path,
      };
    },
    config: {
      id: "G-DCG1Y8HF5J",
      params: {
        send_page_view: true,
      },
    },
  },
  adminRouter
);

app.component('QuillEditor', QuillEditor);

// Register some components
const components = import.meta.glob("./components/global/*.vue", { eager: true });

Object.entries(components).forEach(([path, definition]) => {
  const componentName = path
    .split("/")
    .pop()
    .replace(/\.\w+$/, "");

  app.component(componentName, definition.default);
});

app.component("v-select", vSelect);

// Mont the app
app.mount("#app");
